function resolveWordSeconds(w) {
  if (typeof w.start === 'number' && typeof w.end === 'number') {
    return { start_s: w.start, end_s: w.end };
  }
  if (typeof w.start_ms === 'number' && typeof w.end_ms === 'number') {
    return { start_s: w.start_ms / 1000, end_s: w.end_ms / 1000 };
  }
  return { start_s: Number(w.start ?? w.start_ms ?? 0), end_s: Number(w.end ?? w.end_ms ?? 0) };
}

// ── word_level
export function buildWordTimings(backendWords, totalDurationMs) {
  const words    = [];
  const segments = [];
  const seenSegs = new Map();

  const sorted = [...backendWords].sort((a, b) => {
    const aStart = typeof a.start_ms === 'number' ? a.start_ms : (a.start ?? 0) * 1000;
    const bStart = typeof b.start_ms === 'number' ? b.start_ms : (b.start ?? 0) * 1000;
    return aStart - bStart;
  });

  sorted.forEach((w, index) => {
    const segId = w.div_id ?? `seg_${index}`;
    const { start_s, end_s } = resolveWordSeconds(w);

    if (!seenSegs.has(segId)) {
      seenSegs.set(segId, segments.length);
      segments.push({ id: segId, start_s, speaker: w.speaker ?? null });
    }

    words.push({
      id:          `${segId}_w${index}`,
      segId,
      word:        w.word,
      start_s,
      end_s,
      confidence:  w.confidence ?? null,
      sequence_id: w.sequence_id ?? index,
    });
  });

  const derivedDurationMs = words.length
    ? Math.ceil(words[words.length - 1].end_s * 1000)
    : 0;

  return {
    words,
    segments,
    durationMs: totalDurationMs > 0 ? totalDurationMs : derivedDurationMs,
    mode: 'word_level',
  };
}

// ── sentence_level
export function buildSentenceTimings(backendSegments, totalDurationMs) {
  const words    = [];
  const segments = [];

  backendSegments.forEach((seg, i) => {
    const segId   = seg.div_id ?? `seg_${i}`;
    const startMs = seg.offset_ms ?? 0;
    const endMs   = startMs + (seg.duration_ms ?? 0);

    segments.push({ id: segId, start_s: startMs / 1000, speaker: seg.speaker ?? null });

    words.push({
      id:          `${segId}_w0`,
      segId,
      word:        seg.text ?? '',
      start_s:     startMs / 1000,
      end_s:       endMs / 1000,
      confidence:  null,
      sequence_id: seg.sequence_id ?? i,
    });
  });

  return {
    words,
    segments,
    durationMs: totalDurationMs,
    mode: 'sentence_level',
  };
}

function alignNonSpeechOffsets(nonSpeechItems) {
  if (!Array.isArray(nonSpeechItems) || nonSpeechItems.length === 0) return [];
  return nonSpeechItems
    .slice()
    .sort((a, b) => (a.offset_ms ?? 0) - (b.offset_ms ?? 0))
    .map(item => ({ ...item, offset_ms: Math.max(0, item.offset_ms ?? 0) }));
}

function deriveAudioStartOffsetMs(backendWords) {
  if (!Array.isArray(backendWords) || backendWords.length === 0) return 0;
  let min = Infinity;
  for (const w of backendWords) {
    const v = w.chunk_audio_start_ms;
    if (typeof v === 'number' && v < min) min = v;
  }
  return isFinite(min) ? Math.max(0, min) : 0;
}

// ── entry point
export function transformReplayResponse(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('transformReplayResponse: received null/undefined data');
  }

  const rawNonSpeech = Array.isArray(data.non_speech) ? data.non_speech : [];

  if (data.mode === 'word_level' && Array.isArray(data.words)) {
    const timings = buildWordTimings(data.words, data.total_duration_ms ?? 0);
    const nonSpeech = alignNonSpeechOffsets(rawNonSpeech);
    const audioStartOffsetMs = deriveAudioStartOffsetMs(data.words);
    return { ...timings, non_speech: nonSpeech, audio_url: data.audio_url ?? null, audioStartOffsetMs };
  }

  if (data.mode === 'sentence_level' && Array.isArray(data.segments)) {
    const timings = buildSentenceTimings(data.segments, data.total_duration_ms ?? 0);
    const nonSpeech = alignNonSpeechOffsets(rawNonSpeech);
    return { ...timings, non_speech: nonSpeech, audio_url: data.audio_url ?? null, audioStartOffsetMs: 0 };
  }

  throw new Error(`transformReplayResponse: unknown mode "${data.mode}"`);
}

export function createNonSpeechController(nonSpeechItems, { onShow, onHide, debug = false } = {}) {
  const items = Array.isArray(nonSpeechItems) ? nonSpeechItems : [];
  const shownIds = new Set();

  if (debug) {
    console.debug('[NonSpeechController] initialized with', items.length, 'items:',
      items.map((x, i) => `[${i}] ${x.type} div_id=${x.div_id} offset_ms=${x.offset_ms}`));
  }

  function itemId(item, index) {
    return item.div_id ?? item.sequence_id ?? index;
  }

  function tick(currentMs) {
    items.forEach((item, i) => {
      const id = itemId(item, i);
      if (!shownIds.has(id) && item.offset_ms <= currentMs) {
        shownIds.add(id);
        if (debug) {
          console.debug(`[NonSpeechController] SHOW ${item.type} id=${id} offset=${item.offset_ms}ms currentMs=${currentMs}`);
        }
        onShow?.(item);
      }
    });
  }

  function syncToPosition(currentMs) {
    if (debug) {
      console.debug('[NonSpeechController] syncToPosition', currentMs, 'ms');
    }
    items.forEach((item, i) => {
      const id = itemId(item, i);
      if (item.offset_ms <= currentMs) {
        if (!shownIds.has(id)) {
          shownIds.add(id);
          if (debug) {
            console.debug(`[NonSpeechController] SHOW (seek) ${item.type} id=${id}`);
          }
          onShow?.(item);
        }
      } else {
        if (shownIds.has(id)) {
          shownIds.delete(id);
          if (debug) {
            console.debug(`[NonSpeechController] HIDE (seek back) ${item.type} id=${id}`);
          }
          onHide?.(item);
        }
      }
    });
  }

  function attach(audioEl) {
    syncToPosition(audioEl.currentTime * 1000);
    audioEl.addEventListener('seeked', () => syncToPosition(audioEl.currentTime * 1000));
    audioEl.addEventListener('ended', () => {
      shownIds.clear();
      items.forEach(item => onHide?.(item));
    });
  }

  function reset() {
    shownIds.clear();
    items.forEach(item => onHide?.(item));
  }

  return { tick, attach, syncToPosition, reset };
}